import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { sendNotification } from '@/lib/notifications';
import { AppShell3 } from '@/components/app-shell-3';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  ArrowLeft,
  FileText,
  FileUp,
  Image as ImageIcon,
  Video,
  Upload,
  X,
  Building2,
  Grid3X3,
  Check,
  ChevronsUpDown,
  Camera,
  Pause,
  Play,
  Square,
  RotateCcw,
  CircleDot,
  CheckCircle2,
  Edit2,
  Eye,
  Trash2,
  Plus,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { AiEditor } from '@/ai-editor';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { DocumentUpload } from '@/components/DocumentUpload';
import { PageTransition } from '@/components/PageTransition';

// Helper function to format timer
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function CreatePostPage() {
  const { projectId, stackId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Create Post');

  const isGlobalView = !projectId && !stackId;
  const isStackView = !!stackId;
  const isSingleSpaceView = !!projectId && !stackId;

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [space, setSpace] = useState(null);
  const [stackProjects, setStackProjects] = useState([]);
  const [allAvailableSpaces, setAllAvailableSpaces] = useState([]);
  const [visibilityType, setVisibilityType] = useState(
    isStackView ? 'stack' : isSingleSpaceView ? 'space' : 'company'
  );
  const [postType, setPostType] = useState('text');
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingMessage, setUploadingMessage] = useState('');
  const [recordingState, setRecordingState] = useState('idle');
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const [videoHeight, setVideoHeight] = useState(400);

  const [timerInterval, setTimerInterval] = useState(null);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState(null);

  // Video element refs to prevent flickering
  const videoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const uploadedVideoRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    linkUrl: '',
    imageUrl: '',
    videoUrl: '',
    audioUrl: '',
    imageCaption: '',
    documentUrl: '',
    documentName: '',
    documentType: '',
    documentSize: 0,
    documentText: '',
    selectedProjectId: projectId || null,
    selectedProjectIds: [],
    requiresReading: false,
    requiresUnderstanding: false,
    generateQuiz: false
  });

  const [quizData, setQuizData] = useState(null);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [isEditingQuiz, setIsEditingQuiz] = useState(false);
  const [questionTypeCache, setQuestionTypeCache] = useState({});
  const [transcribing, setTranscribing] = useState(false);
  const [extractingDocumentText, setExtractingDocumentText] = useState(false);

  // Load necessary data
  useEffect(() => {
    loadData();
  }, [projectId, stackId, company?.id]);

  // Handle video stream connection to prevent flickering
  useEffect(() => {
    if (videoRef.current && mediaStream) {
      // Only set srcObject if it's not already set or if it's different
      if (videoRef.current.srcObject !== mediaStream) {
        videoRef.current.srcObject = mediaStream;
      }
    }
  }, [mediaStream, recordingState]);

  // Handle recorded blob URL to prevent flickering
  useEffect(() => {
    if (recordedBlob) {
      // Create blob URL only once when blob changes
      const url = URL.createObjectURL(recordedBlob);
      setRecordedBlobUrl(url);

      // Cleanup function to revoke the URL when component unmounts or blob changes
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setRecordedBlobUrl(null);
    }
  }, [recordedBlob]);

  // Track video height for transcription panel
  useEffect(() => {
    const videoElement = uploadedVideoRef.current;
    if (!videoElement) return;

    const updateHeight = () => {
      const height = videoElement.offsetHeight;
      if (height > 0) {
        setVideoHeight(height);
      }
    };

    // Update height when video loads
    videoElement.addEventListener('loadedmetadata', updateHeight);

    // Use ResizeObserver to track size changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(videoElement);

    // Initial update
    updateHeight();

    return () => {
      videoElement.removeEventListener('loadedmetadata', updateHeight);
      resizeObserver.disconnect();
    };
  }, [videoPreview, formData.videoUrl]);

  const loadData = async () => {
    if (!company?.id) return;

    try {
      setLoading(true);

      if (isSingleSpaceView) {
        // Load single space
        const { data: spaceData } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        setSpace(spaceData);
        setFormData(prev => ({ ...prev, selectedProjectId: projectId, selectedProjectIds: [projectId] }));

        // Load all company spaces for cross-posting
        const { data: companySpaces } = await supabase
          .from('projects')
          .select('id, name')
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .order('name');

        setAllAvailableSpaces(companySpaces || []);
      } else if (isStackView) {
        // Load stack projects
        const { data: stackData } = await supabase
          .from('project_stacks')
          .select('project_ids')
          .eq('id', stackId)
          .single();

        if (stackData?.project_ids) {
          const { data: projects } = await supabase
            .from('projects')
            .select('*')
            .in('id', stackData.project_ids);

          setStackProjects(projects || []);
          setFormData(prev => ({
            ...prev,
            selectedProjectIds: stackData.project_ids
          }));
        }
      } else {
        // Load all company spaces for global view
        const { data: companySpaces, error: spacesError } = await supabase
          .from('projects')
          .select('id, name')
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .order('name');

        if (spacesError) {
          console.error('Error fetching available spaces:', spacesError);
        }

        setAllAvailableSpaces(companySpaces || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load spaces');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (content) => {
    setFormData({ ...formData, content });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Image must be less than 100MB');
      return;
    }

    setUploadingImage(true);
    setUploadProgress(0);
    setUploadingMessage('Preparing upload...');

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `message-board-${projectId || 'global'}-${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;

      // Get signed upload URL from Edge Function
      setUploadingMessage('Getting upload URL...');
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        'generate-upload-url',
        {
          body: {
            fileName: fileName,
            contentType: file.type,
          },
        }
      );

      if (urlError || !urlData?.signedUrl || !urlData?.publicUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Upload to GCS using XMLHttpRequest for progress tracking
      setUploadingMessage('Uploading image...');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        xhr.open('PUT', urlData.signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      setFormData({ ...formData, imageUrl: urlData.publicUrl });
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
      setUploadingMessage('');
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData({ ...formData, imageUrl: '', imageCaption: '' });
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video must be less than 500MB');
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);
    setUploadingMessage('Preparing upload...');

    try {
      // Generate temporary post ID for folder structure
      const tempPostId = `post-${Date.now()}-${Math.random().toString(36).substring(2)}`;
      const folderPath = `${company.id}/${tempPostId}`;
      const fileExt = file.name.split('.').pop();

      // Get signed upload URL for video from Edge Function
      setUploadingMessage('Getting video upload URL...');
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        'generate-upload-url',
        {
          body: {
            fileName: `video.${fileExt}`,
            contentType: file.type,
            folderPath: folderPath,
          },
        }
      );

      if (urlError || !urlData?.signedUrl || !urlData?.publicUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Upload video to GCS using XMLHttpRequest for progress tracking
      setUploadingMessage('Uploading video...');

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        xhr.open('PUT', urlData.signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      setFormData({
        ...formData,
        videoUrl: urlData.publicUrl,
        audioUrl: null
      });
      setVideoPreview(URL.createObjectURL(file));

      // If quiz generation is enabled, get transcription only (quiz will be generated later)
      if (formData.generateQuiz) {
        try {
          await getTranscriptionOnly(urlData.publicUrl);
        } catch (error) {
          // Continue even if transcription fails
          console.error('Transcription failed:', error);
        }
      }

      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.message || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      setUploadingMessage('');
    }
  };

  // Recording functions
  const openRecordingStudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });

      setMediaStream(stream);
      setRecordingState('preview');
      toast.success('Camera ready');
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Failed to access camera/microphone. Please check permissions.');
      setRecordingState('idle');
    }
  };

  const startRecording = () => {
    if (!mediaStream) return;

    try {
      const chunks = [];
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm'
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedBlob(blob);
        setRecordingState('reviewing');

        if (timerInterval) {
          clearInterval(timerInterval);
          setTimerInterval(null);
        }
      };

      setMediaRecorder(recorder);
      setRecordedChunks(chunks);
      recorder.start(100);
      setRecordingState('recording');
      setRecordingTimer(0);

      const interval = setInterval(() => {
        setRecordingTimer(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setRecordingState('paused');
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      toast.info('Recording paused');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setRecordingState('recording');

      const interval = setInterval(() => {
        setRecordingTimer(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);

      toast.success('Recording resumed');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    setRecordingTimer(0);
    setRecordedChunks([]);
    setRecordingState('preview');
    toast.info('Recording discarded');
  };

  const useRecording = async () => {
    if (!recordedBlob) return;

    setUploadingVideo(true);
    setUploadProgress(0);
    setUploadingMessage('Preparing upload...');

    try {
      // Generate temporary post ID for folder structure
      const tempPostId = `post-${Date.now()}-${Math.random().toString(36).substring(2)}`;
      const folderPath = `${company.id}/${tempPostId}`;

      // Upload video file
      setUploadingMessage('Getting video upload URL...');
      const { data: videoUrlData, error: videoUrlError } = await supabase.functions.invoke(
        'generate-upload-url',
        {
          body: {
            fileName: `video.webm`,
            contentType: 'video/webm',
            folderPath: folderPath,
          },
        }
      );

      if (videoUrlError || !videoUrlData?.signedUrl || !videoUrlData?.publicUrl) {
        throw new Error('Failed to get video upload URL');
      }

      // Upload video to GCS with progress tracking
      setUploadingMessage('Uploading video...');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error('Video upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Video upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Video upload cancelled')));

        xhr.open('PUT', videoUrlData.signedUrl);
        xhr.setRequestHeader('Content-Type', 'video/webm');
        xhr.send(recordedBlob);
      });

      setFormData({
        ...formData,
        videoUrl: videoUrlData.publicUrl,
        audioUrl: null
      });
      setVideoPreview(URL.createObjectURL(recordedBlob));

      // If quiz generation is enabled, get transcription only (quiz will be generated later)
      if (formData.generateQuiz) {
        try {
          await getTranscriptionOnly(videoUrlData.publicUrl);
        } catch (error) {
          // Continue even if transcription fails
          console.error('Transcription failed:', error);
        }
      }

      closeRecordingStudio();
      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.message || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      setUploadingMessage('');
    }
  };

  // Helper function to get transcription only (no quiz generation)
  const getTranscriptionOnly = async (videoUrl) => {
    setTranscribing(true);
    setUploadingMessage('Transcribing audio...');

    try {
      const response = await fetch('https://n8n.add.ai/webhook/transcribeaudio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoUrl: videoUrl,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Transcription error response:', errorText);
        throw new Error(`Failed to get transcription: ${response.status}`);
      }

      const data = await response.json();
      console.log('Transcription response:', data);

      // Store transcription data - new webhook returns 'text' and 'words'
      setFormData(prev => ({
        ...prev,
        transcriptionRaw: data.text || data.transcription_raw || data.transcriptionRaw || '',
        transcriptionTimestamps: data.words || data.transcription_timestamp || data.transcriptionTimestamps || [],
        n8nFullResponse: data
      }));

      toast.success('Transcription completed!');
      return data;
    } catch (error) {
      console.error('Error getting transcription:', error);
      toast.error('Failed to get transcription. You can still create the post.');
      throw error;
    } finally {
      setTranscribing(false);
      setUploadingMessage('');
    }
  };

  // Helper function to extract text from document via N8N webhook
  const extractDocumentText = async (documentUrl, documentName, documentType) => {
    setExtractingDocumentText(true);
    setUploadingMessage('Extracting document text...');

    try {
      const response = await fetch('https://n8n.add.ai/webhook/extract-document-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentUrl: documentUrl,
          documentName: documentName,
          documentType: documentType,
          companyId: company?.id,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Document extraction error response:', errorText);
        throw new Error(`Failed to extract document text: ${response.status}`);
      }

      const data = await response.json();
      console.log('Document extraction response:', data);

      // Handle different response formats from webhook
      // Expected: { text: "..." } but might also get { extractedText: "..." } or { content: "..." }
      const extractedText = data.text || data.extractedText || data.content || data.document_text || '';

      if (!extractedText) {
        console.warn('No text found in extraction response:', data);
        toast.warning('Could not extract text from document. Quiz generation may not work.');
      } else {
        toast.success('Document text extracted!');
      }

      setFormData(prev => ({
        ...prev,
        documentText: extractedText
      }));

      return data;
    } catch (error) {
      console.error('Error extracting document text:', error);
      toast.error('Failed to extract document text. You can still create the post.');
      throw error;
    } finally {
      setExtractingDocumentText(false);
      setUploadingMessage('');
    }
  };

  // Helper function to generate quiz from video via N8N webhook
  const generateQuizFromAudio = async (audioUrl, videoUrl) => {
    setGeneratingQuiz(true);
    setUploadingMessage('Generating quiz...');

    try {
      const response = await fetch('https://yqmlqctpakqxtwzrdofl.supabase.co/functions/v1/proxy-quiz-generator', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabase.supabaseKey}`,
        },
        body: JSON.stringify({
          videoUrl: videoUrl,
          postTitle: formData.title,
        }),
      });

      console.log('N8N response status:', response.status);
      console.log('N8N response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('N8N error response:', errorText);
        throw new Error(`Failed to generate quiz: ${response.status}`);
      }

      // Get the response as text first to see what we're dealing with
      const responseText = await response.text();
      console.log('N8N raw response text:', responseText);

      // Try to parse it as JSON
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('N8N parsed JSON response:', data);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        console.error('Response text was:', responseText);
        throw new Error('N8N returned invalid JSON');
      }

      // Parse the N8N response - new format includes quiz, transcriptions, and metadata
      console.log('N8N response data:', data);

      // Extract quiz, transcription_raw, and transcription_timestamp from response
      let quizContent = null;
      let transcriptionRaw = null;
      let transcriptionTimestamps = null;

      // New format: { quiz: "JSON string", transcription_raw: "text", transcription_timestamp: [...] }
      if (data && data.quiz) {
        try {
          quizContent = JSON.parse(data.quiz);
          transcriptionRaw = data.transcription_raw || null;
          transcriptionTimestamps = data.transcription_timestamp || null;
          console.log('Parsed new N8N format:', { quizContent, transcriptionRaw, transcriptionTimestamps });
        } catch (parseError) {
          console.error('Failed to parse quiz JSON from N8N response:', parseError);
          throw new Error('Invalid quiz JSON in response');
        }
      }
      // Legacy format handling for backwards compatibility
      else if (data && Array.isArray(data) && data.length > 0 && data[0].message && data[0].message.content) {
        quizContent = JSON.parse(data[0].message.content);
        console.log('Parsed quiz content from array (legacy):', quizContent);
      }
      else if (data && data.message && data.message.content) {
        quizContent = JSON.parse(data.message.content);
        console.log('Parsed quiz content from object (legacy):', quizContent);
      }
      else if (data && typeof data === 'object' && data.quizTitle) {
        quizContent = data;
        console.log('Quiz data returned directly (legacy):', quizContent);
      }

      // If we successfully got quiz content, normalize and set it
      if (quizContent) {
        // Normalize the field names (correctAnswer -> correct_answer) and validate
        if (quizContent.questions) {
          const validQuestions = quizContent.questions
            .map(q => ({
              ...q,
              correct_answer: q.correctAnswer || q.correct_answer,
              type: q.type,
              question: q.question,
              options: q.options,
              explanation: q.explanation
            }))
            .filter((q, index) => {
              // Validate that question has required fields
              const hasCorrectAnswer = q.correct_answer !== undefined && q.correct_answer !== null && q.correct_answer !== '';
              const hasQuestion = q.question && q.question.trim() !== '';
              const hasType = q.type && q.type.trim() !== '';

              if (!hasCorrectAnswer || !hasQuestion || !hasType) {
                console.warn(`Question ${index + 1} is missing required fields:`, {
                  hasCorrectAnswer,
                  hasQuestion,
                  hasType,
                  question: q
                });
                toast.error(`Question ${index + 1} is invalid and was removed from the quiz`);
                return false;
              }
              return true;
            });

          quizContent.questions = validQuestions;
        }

        console.log('Normalized and validated quiz content:', quizContent);

        // Store quiz data and transcriptions in form state
        setQuizData(quizContent);
        setFormData(prev => ({
          ...prev,
          transcriptionRaw: transcriptionRaw,
          transcriptionTimestamps: transcriptionTimestamps,
          n8nFullResponse: data // Store full response for GCS backup
        }));

        toast.success('Quiz and transcription generated successfully!');
      } else {
        console.error('Unexpected response format:', data);
        throw new Error('Invalid quiz data received');
      }
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast.error('Failed to generate quiz. You can still post without a quiz.');
    } finally {
      setGeneratingQuiz(false);
      setUploadingMessage('');
    }
  };

  // Helper function to extract audio from video blob
  const extractAudioFromVideo = async (videoBlob) => {
    return new Promise((resolve, reject) => {
      const videoElement = document.createElement('video');
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const mediaStreamDestination = audioContext.createMediaStreamDestination();

      videoElement.src = URL.createObjectURL(videoBlob);
      videoElement.muted = true;

      videoElement.addEventListener('loadedmetadata', async () => {
        try {
          const source = audioContext.createMediaElementSource(videoElement);
          source.connect(mediaStreamDestination);

          const audioRecorder = new MediaRecorder(mediaStreamDestination.stream, {
            mimeType: 'audio/webm'
          });

          const audioChunks = [];

          audioRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              audioChunks.push(e.data);
            }
          };

          audioRecorder.onstop = async () => {
            const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });

            // Convert webm audio to mp3 using MediaRecorder fallback
            // Note: Browser will handle the conversion best-effort
            const mp3Blob = new Blob([audioBlob], { type: 'audio/mpeg' });

            URL.revokeObjectURL(videoElement.src);
            resolve(mp3Blob);
          };

          audioRecorder.start();
          await videoElement.play();

          videoElement.onended = () => {
            audioRecorder.stop();
            source.disconnect();
          };
        } catch (error) {
          URL.revokeObjectURL(videoElement.src);
          reject(error);
        }
      });

      videoElement.addEventListener('error', () => {
        URL.revokeObjectURL(videoElement.src);
        reject(new Error('Failed to load video for audio extraction'));
      });
    });
  };

  const closeRecordingStudio = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setMediaRecorder(null);
    setRecordedChunks([]);
    setRecordedBlob(null);
    setRecordingTimer(0);
    setRecordingState('idle');
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (postType === 'text' && !formData.content.trim()) {
      toast.error('Please enter some content');
      return;
    }

    if (postType === 'document' && !formData.documentUrl) {
      toast.error('Please upload a document');
      return;
    }

    if (postType === 'image' && !formData.imageUrl) {
      toast.error('Please upload an image');
      return;
    }

    if (postType === 'video' && !formData.videoUrl) {
      toast.error('Please upload a video');
      return;
    }

    if (visibilityType === 'space' && (!formData.selectedProjectIds || formData.selectedProjectIds.length === 0)) {
      toast.error('Please select at least one space');
      return;
    }

    // If quiz generation is enabled, redirect to quiz generation page
    if (formData.generateQuiz && (postType === 'video' || postType === 'document' || postType === 'text')) {
      // Check if transcription is available for video
      if (postType === 'video' && !formData.transcriptionRaw) {
        toast.error('Please wait for video transcription to complete');
        return;
      }
      // For documents, we just need the document URL (quiz generator will extract text)
      if (postType === 'document' && !formData.documentUrl) {
        toast.error('Please upload a document first');
        return;
      }
      // For text posts, we need content
      if (postType === 'text' && !formData.content) {
        toast.error('Please add some content first');
        return;
      }

      // Store current form data in sessionStorage and navigate to quiz generation page
      sessionStorage.setItem('quizGenerationData', JSON.stringify({
        formData,
        postType,
        visibilityType,
        projectId,
        stackId
      }));

      // Navigate to quiz generation page
      navigate('/posts/generate-quiz');
      return;
    }

    setSubmitting(true);

    try {
      // If we have N8N response data and a video URL, save metadata.json to GCS
      let metadataUrl = null;
      if (formData.n8nFullResponse && formData.videoUrl) {
        try {
          // Extract folder path from video URL
          const videoUrl = formData.videoUrl;
          const urlParts = videoUrl.split('/');
          const bucketIndex = urlParts.findIndex(part => part === 'training-matrix-videos');
          if (bucketIndex !== -1 && urlParts.length > bucketIndex + 2) {
            const companyId = urlParts[bucketIndex + 1];
            const postId = urlParts[bucketIndex + 2];
            const folderPath = `${companyId}/${postId}`;

            // Get signed URL for metadata.json
            const { data: metadataUrlData, error: metadataError } = await supabase.functions.invoke(
              'generate-upload-url',
              {
                body: {
                  fileName: 'metadata.json',
                  contentType: 'application/json',
                  folderPath: folderPath,
                },
              }
            );

            if (!metadataError && metadataUrlData?.signedUrl && metadataUrlData?.publicUrl) {
              // Upload metadata JSON to GCS
              const metadataBlob = new Blob([JSON.stringify(formData.n8nFullResponse, null, 2)], {
                type: 'application/json'
              });

              await fetch(metadataUrlData.signedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: metadataBlob
              });

              metadataUrl = metadataUrlData.publicUrl;
              console.log('Metadata saved to GCS:', metadataUrl);
            }
          }
        } catch (metadataErr) {
          console.error('Failed to save metadata to GCS:', metadataErr);
          // Continue with post creation even if metadata upload fails
        }
      }

      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        created_by: userProfile.id,
        company_id: company.id,
        post_type: postType,
        link_url: null, // Legacy field, kept for backwards compatibility
        image_url: postType === 'image' ? formData.imageUrl : null,
        image_caption: postType === 'image' ? formData.imageCaption.trim() : null,
        video_url: postType === 'video' ? formData.videoUrl : null,
        audio_url: postType === 'video' ? formData.audioUrl : null,
        document_url: postType === 'document' ? formData.documentUrl : null,
        document_name: postType === 'document' ? formData.documentName : null,
        document_type: postType === 'document' ? formData.documentType : null,
        document_size: postType === 'document' ? formData.documentSize : null,
        document_text: postType === 'document' ? formData.documentText : null,
        selected_project_ids: [],
        requires_reading: formData.requiresReading,
        requires_understanding: formData.requiresUnderstanding,
        quiz_data: quizData,
        has_quiz: !!quizData,
        transcription_raw: formData.transcriptionRaw || null,
        transcription_timestamps: formData.transcriptionTimestamps || null,
        metadata_url: metadataUrl
      };

      // Set project_id and selected_project_ids based on visibility
      if (visibilityType === 'company') {
        postData.project_id = null;
        postData.selected_project_ids = [];
      } else if (visibilityType === 'stack' && isStackView) {
        postData.project_id = null;
        postData.selected_project_ids = stackProjects.map(p => p.id);
      } else if (visibilityType === 'space') {
        if (isSingleSpaceView) {
          postData.project_id = projectId;
          postData.selected_project_ids = [projectId];
        } else {
          postData.project_id = formData.selectedProjectIds[0] || null;
          postData.selected_project_ids = formData.selectedProjectIds;
        }
      }

      const { data: newPost, error } = await supabase
        .from('message_board_posts')
        .insert([postData])
        .select()
        .single();

      if (error) throw error;

      // Send notifications if tracking is required
      if (formData.requiresReading || formData.requiresUnderstanding) {
        let recipientIds = [];

        if (visibilityType === 'company') {
          const { data: members } = await supabase
            .from('company_members')
            .select('user_id')
            .eq('company_id', company.id)
            .neq('user_id', userProfile.id);

          recipientIds = members?.map(m => m.user_id) || [];
        } else {
          const projectIds = postData.selected_project_ids.length > 0
            ? postData.selected_project_ids
            : [postData.project_id].filter(Boolean);

          if (projectIds.length > 0) {
            const { data: members } = await supabase
              .from('team_members')
              .select('user_id')
              .in('project_id', projectIds)
              .neq('user_id', userProfile.id);

            recipientIds = [...new Set(members?.map(m => m.user_id) || [])];
          }
        }

        // Send notifications
        for (const recipientId of recipientIds) {
          await sendNotification({
            recipient_id: recipientId,
            sender_id: userProfile.id,
            type: 'message_board_post',
            title: 'New Message Board Post',
            message: `${userProfile.first_name} ${userProfile.last_name} posted: ${formData.title}`,
            link: `/message-board`,
            project_id: postData.project_id
          });
        }
      }

      toast.success('Post created successfully');

      // Navigate back
      if (isSingleSpaceView) {
        navigate(`/space/${projectId}/message-board`);
      } else if (isStackView) {
        navigate(`/stack/${stackId}/message-board`);
      } else {
        navigate('/posts');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clean up recording if active
    closeRecordingStudio();

    // Navigate back
    if (isSingleSpaceView) {
      navigate(`/space/${projectId}/message-board`);
    } else if (isStackView) {
      navigate(`/stack/${stackId}/message-board`);
    } else {
      navigate('/posts');
    }
  };

  if (loading) {
    return (
      <AppShell3>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <div className="min-h-screen bg-gray-50 pb-24">
          {/* Header */}
          <div className="bg-white border-b">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Message Board
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Create New Post</h1>
              <p className="text-gray-600 mt-1">
                Share an update or start a discussion with your team
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Visibility Selection */}
                <div>
                  <label className='text-sm font-medium mb-2 block'>
                    Who can see this post?
                  </label>
                  <div className='flex gap-2 p-1 bg-muted rounded-lg'>
                    <button
                      type='button'
                      onClick={() => {
                        setVisibilityType('company');
                        setFormData({
                          ...formData,
                          selectedProjectId: null,
                          selectedProjectIds: []
                        });
                      }}
                      className={`flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-md transition-colors ${
                        visibilityType === 'company'
                          ? 'bg-background shadow-sm'
                          : 'hover:bg-background/50'
                      }`}
                    >
                      <Building2 className='h-5 w-5' />
                      <span className='text-xs font-medium'>All Spaces</span>
                    </button>
                    <button
                      type='button'
                      onClick={() => {
                        setVisibilityType('space');
                        // Re-preselect current space when switching back
                        if (isSingleSpaceView && projectId) {
                          setFormData(prev => ({
                            ...prev,
                            selectedProjectId: projectId,
                            selectedProjectIds: prev.selectedProjectIds?.length > 0 ? prev.selectedProjectIds : [projectId]
                          }));
                        }
                      }}
                      className={`flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-md transition-colors ${
                        visibilityType === 'space'
                          ? 'bg-background shadow-sm'
                          : 'hover:bg-background/50'
                      }`}
                    >
                      <Grid3X3 className='h-5 w-5' />
                      <span className='text-xs font-medium'>Select Space(s)</span>
                    </button>
                  </div>
                </div>

                {/* Multi-select Space Selector */}
                {visibilityType === 'space' && (
                  <div>
                    <label className='text-sm font-medium mb-1 block'>
                      Select Space(s)
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between"
                        >
                          {formData.selectedProjectIds && formData.selectedProjectIds.length > 0
                            ? `${formData.selectedProjectIds.length} space${formData.selectedProjectIds.length !== 1 ? 's' : ''} selected`
                            : "Select spaces..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search spaces..." />
                          <CommandList>
                            <CommandEmpty>No spaces found.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {(isStackView ? stackProjects : allAvailableSpaces).map(proj => {
                                const isSelected = (formData.selectedProjectIds || []).includes(proj.id);
                                return (
                                  <CommandItem
                                    key={proj.id}
                                    value={proj.name}
                                    onSelect={() => {
                                      const currentIds = formData.selectedProjectIds || [];
                                      const newIds = currentIds.includes(proj.id)
                                        ? currentIds.filter(id => id !== proj.id)
                                        : [...currentIds, proj.id];
                                      setFormData({
                                        ...formData,
                                        selectedProjectIds: newIds,
                                        selectedProjectId: newIds.length === 1 ? newIds[0] : null
                                      });
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        isSelected ? "opacity-100" : "opacity-0"
                                      }`}
                                    />
                                    {proj.name}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {formData.selectedProjectIds && formData.selectedProjectIds.length > 0 && (
                      <div className='flex flex-wrap gap-2 mt-2'>
                        {formData.selectedProjectIds.map(projId => {
                          const proj = (isStackView ? stackProjects : allAvailableSpaces).find(p => p.id === projId);
                          return proj ? (
                            <Badge key={projId} variant="secondary" className="gap-1 pr-1">
                              <span>{proj.name}</span>
                              <button
                                type="button"
                                className="ml-1 hover:bg-destructive/20 rounded-sm p-0.5 transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newIds = formData.selectedProjectIds.filter(id => id !== projId);
                                  setFormData({
                                    ...formData,
                                    selectedProjectIds: newIds,
                                    selectedProjectId: newIds.length === 1 ? newIds[0] : null
                                  });
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Post Type Selection */}
                <div>
                  <label className='text-sm font-medium mb-2 block'>Post Type</label>
                  <div className='flex gap-2 p-1 bg-muted rounded-lg'>
                    <button
                      type='button'
                      onClick={() => setPostType('text')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                        postType === 'text'
                          ? 'bg-background shadow-sm'
                          : 'hover:bg-background/50'
                      }`}
                    >
                      <FileText className='h-4 w-4' />
                      <span className='text-sm font-medium'>Text</span>
                    </button>
                    <button
                      type='button'
                      onClick={() => setPostType('document')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                        postType === 'document'
                          ? 'bg-background shadow-sm'
                          : 'hover:bg-background/50'
                      }`}
                    >
                      <FileUp className='h-4 w-4' />
                      <span className='text-sm font-medium'>Document</span>
                    </button>
                    <button
                      type='button'
                      onClick={() => setPostType('image')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                        postType === 'image'
                          ? 'bg-background shadow-sm'
                          : 'hover:bg-background/50'
                      }`}
                    >
                      <ImageIcon className='h-4 w-4' />
                      <span className='text-sm font-medium'>Image</span>
                    </button>
                    <button
                      type='button'
                      onClick={() => setPostType('video')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                        postType === 'video'
                          ? 'bg-background shadow-sm'
                          : 'hover:bg-background/50'
                      }`}
                    >
                      <Video className='h-4 w-4' />
                      <span className='text-sm font-medium'>Video</span>
                    </button>
                  </div>
                </div>

                {/* Tracking Requirements */}
                <div>
                  <label className='text-sm font-medium mb-3 block'>
                    Tracking Requirements
                  </label>
                  <div className='space-y-3'>
                    <div className='flex items-center justify-between p-3 border rounded-lg'>
                      <div className='flex-1'>
                        <label
                          htmlFor='requires-reading'
                          className='text-sm font-medium cursor-pointer'
                        >
                          Require reading
                        </label>
                        <p className='text-xs text-muted-foreground mt-0.5'>
                          Users must open this post
                        </p>
                      </div>
                      <Switch
                        id='requires-reading'
                        checked={formData.requiresReading}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            requiresReading: checked
                          })
                        }
                      />
                    </div>

                    <div className='flex items-center justify-between p-3 border rounded-lg'>
                      <div className='flex-1'>
                        <label
                          htmlFor='requires-understanding'
                          className='text-sm font-medium cursor-pointer'
                        >
                          Require understanding
                        </label>
                        <p className='text-xs text-muted-foreground mt-0.5'>
                          Users must confirm they understand
                        </p>
                      </div>
                      <Switch
                        id='requires-understanding'
                        checked={formData.requiresUnderstanding}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            requiresUnderstanding: checked
                          })
                        }
                      />
                    </div>

                    {(postType === 'video' || postType === 'document' || postType === 'text') && (
                      <div className='flex items-center justify-between p-3 border rounded-lg'>
                        <div className='flex-1'>
                          <label
                            htmlFor='generate-quiz'
                            className='text-sm font-medium cursor-pointer'
                          >
                            Generate quiz from {postType === 'text' ? 'content' : postType}
                          </label>
                          <p className='text-xs text-muted-foreground mt-0.5'>
                            AI will create a quiz for users to complete
                          </p>
                        </div>
                        <Switch
                          id='generate-quiz'
                          checked={formData.generateQuiz}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              generateQuiz: checked
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Title Input */}
                <div>
                  <label className='text-sm font-medium mb-1 block'>Title</label>
                  <Input
                    placeholder='Enter post title'
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                {/* Text Post Content */}
                {postType === 'text' && (
                  <div>
                    <label className='text-sm font-medium mb-2 block'>Content</label>
                    <AiEditor
                      value={formData.content}
                      onChange={handleContentChange}
                      placeholder='Start writing your post...'
                    />
                  </div>
                )}

                {/* Document Post */}
                {postType === 'document' && (
                  <div className='space-y-3'>
                    <div>
                      <label className='text-sm font-medium mb-2 block'>Document</label>
                      <DocumentUpload
                        companyId={company?.id}
                        uploadedDocument={formData.documentUrl ? {
                          url: formData.documentUrl,
                          name: formData.documentName,
                          type: formData.documentType,
                          size: formData.documentSize
                        } : null}
                        onUploadComplete={(docData) => {
                          setFormData({
                            ...formData,
                            documentUrl: docData.url,
                            documentName: docData.name,
                            documentType: docData.type,
                            documentSize: docData.size,
                            documentText: ''
                          });
                        }}
                        onRemove={() => {
                          setFormData({
                            ...formData,
                            documentUrl: '',
                            documentName: '',
                            documentType: '',
                            documentSize: 0,
                            documentText: ''
                          });
                        }}
                        disabled={extractingDocumentText}
                      />
                      {/* Extracting text indicator */}
                      {extractingDocumentText && (
                        <div className='flex items-center gap-2 mt-2 text-sm text-muted-foreground'>
                          <LoadingSpinner className='h-4 w-4' />
                          <span>Extracting document text for AI features...</span>
                        </div>
                      )}
                      {/* Show extracted text status */}
                      {formData.documentText && !extractingDocumentText && (
                        <div className='flex items-center gap-2 mt-2 text-sm text-green-600'>
                          <CheckCircle2 className='h-4 w-4' />
                          <span>Document text extracted ({formData.documentText.split(/\s+/).length} words)</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className='text-sm font-medium mb-1 block'>
                        Description (optional)
                      </label>
                      <Textarea
                        placeholder='Add a description about this document...'
                        value={formData.content}
                        onChange={e => setFormData({ ...formData, content: e.target.value })}
                        rows={4}
                      />
                    </div>
                  </div>
                )}

                {/* Image Post */}
                {postType === 'image' && (
                  <div className='space-y-3'>
                    {!imagePreview ? (
                      <div className='border-2 border-dashed rounded-lg p-8 text-center'>
                        <input
                          type='file'
                          accept='image/*'
                          onChange={handleImageUpload}
                          className='hidden'
                          id='image-upload'
                          disabled={uploadingImage}
                        />
                        <label
                          htmlFor='image-upload'
                          className='cursor-pointer flex flex-col items-center gap-2'
                        >
                          {uploadingImage ? (
                            <LoadingSpinner />
                          ) : (
                            <>
                              <Upload className='h-12 w-12 text-muted-foreground' />
                              <div>
                                <p className='font-medium'>Click to upload image</p>
                                <p className='text-sm text-muted-foreground'>
                                  PNG, JPG, GIF up to 5MB
                                </p>
                              </div>
                            </>
                          )}
                        </label>
                      </div>
                    ) : (
                      <>
                        <div className='relative rounded-lg overflow-hidden border'>
                          <img
                            src={imagePreview}
                            alt='Preview'
                            className='w-full h-auto max-h-96 object-contain bg-muted'
                          />
                          <button
                            type='button'
                            onClick={removeImage}
                            className='absolute top-2 right-2 p-1 bg-background/90 hover:bg-background rounded-full shadow-lg'
                          >
                            <X className='h-4 w-4' />
                          </button>
                        </div>
                        <div>
                          <label className='text-sm font-medium mb-1 block'>
                            Caption (optional)
                          </label>
                          <Textarea
                            placeholder='Add a caption or description...'
                            value={formData.imageCaption}
                            onChange={e =>
                              setFormData({ ...formData, imageCaption: e.target.value })
                            }
                            rows={3}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Video Post */}
                {postType === 'video' && (
                  <div className='space-y-3'>
                    {/* IDLE STATE */}
                    {recordingState === 'idle' && !videoPreview && (
                      <div className='space-y-3'>
                        <div className='border-2 border-dashed rounded-lg p-8 text-center'>
                          <input
                            type='file'
                            accept='video/*'
                            onChange={handleVideoUpload}
                            className='hidden'
                            id='video-upload'
                            disabled={uploadingVideo}
                          />
                          <label
                            htmlFor='video-upload'
                            className='cursor-pointer flex flex-col items-center gap-2 w-full'
                          >
                            {uploadingVideo ? (
                              <div className='flex flex-col items-center gap-2 w-full px-4'>
                                <LoadingSpinner />
                                <p className='text-sm font-medium'>{uploadingMessage}</p>
                                {uploadProgress > 0 && (
                                  <div className='w-full max-w-xs'>
                                    <div className='flex justify-between text-xs text-muted-foreground mb-1'>
                                      <span>Uploading...</span>
                                      <span>{uploadProgress}%</span>
                                    </div>
                                    <div className='w-full bg-muted rounded-full h-2'>
                                      <div
                                        className='bg-primary h-2 rounded-full transition-all duration-300'
                                        style={{ width: `${uploadProgress}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <Upload className='h-12 w-12 text-muted-foreground' />
                                <div>
                                  <p className='font-medium'>Click to upload video</p>
                                  <p className='text-sm text-muted-foreground'>
                                    MP4, MOV, WebM up to 500MB
                                  </p>
                                </div>
                              </>
                            )}
                          </label>
                        </div>

                        <div className='relative'>
                          <div className='absolute inset-0 flex items-center'>
                            <span className='w-full border-t' />
                          </div>
                          <div className='relative flex justify-center text-xs uppercase'>
                            <span className='bg-background px-2 text-muted-foreground'>Or</span>
                          </div>
                        </div>

                        <Button
                          type='button'
                          variant='outline'
                          onClick={openRecordingStudio}
                          className='w-full h-20 gap-2'
                          disabled={uploadingVideo}
                        >
                          <Camera className='h-6 w-6' />
                          <div className='text-left'>
                            <p className='font-medium'>Record video</p>
                            <p className='text-xs text-muted-foreground'>
                              Use your webcam to record
                            </p>
                          </div>
                        </Button>
                      </div>
                    )}

                    {/* PREVIEW STATE */}
                    {recordingState === 'preview' && (
                      <div className='space-y-3'>
                        <div className='relative rounded-lg overflow-hidden border bg-black'>
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className='w-full h-auto max-h-96'
                          />
                          <div className='absolute top-3 left-3 flex items-center gap-2 bg-background/90 backdrop-blur-sm text-foreground px-3 py-1.5 rounded-full'>
                            <Camera className='h-4 w-4' />
                            <span className='text-sm font-medium'>Camera Ready</span>
                          </div>
                        </div>
                        <div className='flex gap-2'>
                          <Button
                            type='button'
                            onClick={startRecording}
                            className='flex-1 gap-2 bg-red-500 hover:bg-red-600'
                          >
                            <CircleDot className='h-4 w-4' />
                            Start Recording
                          </Button>
                          <Button
                            type='button'
                            variant='outline'
                            onClick={closeRecordingStudio}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* RECORDING STATE */}
                    {recordingState === 'recording' && (
                      <div className='space-y-3'>
                        <div className='relative rounded-lg overflow-hidden border bg-black'>
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className='w-full h-auto max-h-96'
                          />
                          <div className='absolute top-3 left-3 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full shadow-lg'>
                            <div className='h-2.5 w-2.5 bg-white rounded-full animate-pulse' />
                            <span className='text-sm font-bold'>{formatTime(recordingTimer)}</span>
                          </div>
                        </div>
                        <div className='flex gap-2'>
                          <Button
                            type='button'
                            onClick={pauseRecording}
                            variant='outline'
                            className='flex-1 gap-2'
                          >
                            <Pause className='h-4 w-4' />
                            Pause
                          </Button>
                          <Button
                            type='button'
                            onClick={stopRecording}
                            className='flex-1 gap-2 bg-red-500 hover:bg-red-600'
                          >
                            <Square className='h-4 w-4' />
                            Stop Recording
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* PAUSED STATE */}
                    {recordingState === 'paused' && (
                      <div className='space-y-3'>
                        <div className='relative rounded-lg overflow-hidden border bg-black'>
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className='w-full h-auto max-h-96'
                          />
                          <div className='absolute top-3 left-3 flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded-full shadow-lg'>
                            <Pause className='h-3.5 w-3.5' />
                            <span className='text-sm font-bold'>{formatTime(recordingTimer)}</span>
                          </div>
                        </div>
                        <div className='flex gap-2'>
                          <Button
                            type='button'
                            onClick={resumeRecording}
                            className='flex-1 gap-2 bg-green-600 hover:bg-green-700'
                          >
                            <Play className='h-4 w-4' />
                            Resume
                          </Button>
                          <Button
                            type='button'
                            onClick={stopRecording}
                            className='flex-1 gap-2 bg-red-500 hover:bg-red-600'
                          >
                            <Square className='h-4 w-4' />
                            Stop Recording
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* REVIEWING STATE */}
                    {recordingState === 'reviewing' && recordedBlob && recordedBlobUrl && (
                      <div className='space-y-3'>
                        <div className='relative rounded-lg overflow-hidden border'>
                          <video
                            ref={reviewVideoRef}
                            src={recordedBlobUrl}
                            controls
                            className='w-full h-auto max-h-96 bg-black'
                          />
                          <div className='absolute top-3 left-3 flex items-center gap-2 bg-green-500 text-white px-3 py-1.5 rounded-full shadow-lg'>
                            <CheckCircle2 className='h-4 w-4' />
                            <span className='text-sm font-medium'>Review Recording</span>
                          </div>
                        </div>
                        <div className='bg-muted p-3 rounded-lg'>
                          <p className='text-sm text-muted-foreground'>
                            Duration: {formatTime(recordingTimer)}
                          </p>
                        </div>
                        <div className='flex gap-2'>
                          <Button
                            type='button'
                            onClick={useRecording}
                            className='flex-1 gap-2 bg-green-600 hover:bg-green-700'
                            disabled={uploadingVideo}
                          >
                            {uploadingVideo ? (
                              <div className='flex flex-col gap-1 w-full'>
                                <div className='flex items-center justify-center gap-2'>
                                  <LoadingSpinner />
                                  <span>{uploadingMessage}</span>
                                </div>
                                {uploadProgress > 0 && (
                                  <div className='w-full bg-green-900/30 rounded-full h-1.5'>
                                    <div
                                      className='bg-white h-1.5 rounded-full transition-all duration-300'
                                      style={{ width: `${uploadProgress}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <CheckCircle2 className='h-4 w-4' />
                                Use This Recording
                              </>
                            )}
                          </Button>
                          <Button
                            type='button'
                            variant='outline'
                            onClick={discardRecording}
                            className='gap-2'
                            disabled={uploadingVideo}
                          >
                            <RotateCcw className='h-4 w-4' />
                            Re-record
                          </Button>
                          <Button
                            type='button'
                            variant='outline'
                            onClick={closeRecordingStudio}
                            disabled={uploadingVideo}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* UPLOADED STATE */}
                    {videoPreview && recordingState === 'idle' && (
                      <div className='space-y-3'>
                        {/* Video with Transcript Preview Layout (or just video if no quiz) */}
                        {formData.generateQuiz ? (
                          <div className='flex flex-col lg:flex-row gap-4 items-start'>
                            {/* Video Column - 70% */}
                            <div className='lg:w-[70%] w-full rounded-lg overflow-hidden border relative'>
                              <video
                                ref={uploadedVideoRef}
                                src={formData.videoUrl}
                                controls
                                className='w-full h-auto bg-muted'
                              />
                              <button
                                type='button'
                                onClick={() => {
                                  setVideoPreview(null);
                                  setFormData({ ...formData, videoUrl: '', transcriptionRaw: '', transcriptionTimestamps: [] });
                                }}
                                className='absolute top-2 right-2 p-1.5 bg-background/90 hover:bg-background rounded-full shadow-lg transition-colors z-10'
                              >
                                <X className='h-4 w-4' />
                              </button>
                            </div>

                            {/* Transcript Column - 30% */}
                            <div
                              className='lg:w-[30%] w-full rounded-lg border bg-card flex flex-col'
                              style={{ height: `${videoHeight}px` }}
                            >
                              <div className='p-4 border-b bg-muted/50 flex-shrink-0'>
                                <div className='flex items-center gap-2'>
                                  <FileText className='h-4 w-4 text-muted-foreground' />
                                  <h3 className='font-semibold text-sm'>Transcription</h3>
                                </div>
                                <p className='text-xs text-muted-foreground mt-1'>
                                  {transcribing ? 'Processing...' :
                                   formData.transcriptionRaw ? `${formData.transcriptionRaw.split(' ').length} words` :
                                   'Waiting for video upload'}
                                </p>
                              </div>

                              <div className='p-4 bg-muted/30 overflow-y-auto flex-1 min-h-0'>
                                {transcribing ? (
                                  <div className='flex flex-col items-center justify-center h-full gap-3'>
                                    <LoadingSpinner />
                                    <div className='text-center'>
                                      <div className='text-sm font-medium'>Transcribing video...</div>
                                      <div className='text-xs text-muted-foreground mt-1'>
                                        This may take a moment
                                      </div>
                                    </div>
                                  </div>
                                ) : formData.transcriptionRaw ? (
                                  <div>
                                    <div className='flex items-center gap-2 text-green-600 mb-3 pb-3 border-b'>
                                      <CheckCircle2 className='h-4 w-4' />
                                      <span className='text-sm font-medium'>Transcription complete</span>
                                    </div>
                                    <div className='text-sm leading-relaxed whitespace-pre-wrap'>
                                      {formData.transcriptionRaw}
                                    </div>
                                  </div>
                                ) : (
                                  <div className='flex flex-col items-center justify-center h-full gap-2 text-center'>
                                    <Clock className='h-8 w-8 text-muted-foreground' />
                                    <div className='text-sm text-muted-foreground'>
                                      Upload video to generate transcript
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className='relative rounded-lg overflow-hidden border'>
                            <video
                              src={videoPreview}
                              controls
                              className='w-full h-auto max-h-96 bg-black'
                            />
                            <button
                              type='button'
                              onClick={() => {
                                setVideoPreview(null);
                                setFormData({ ...formData, videoUrl: '', transcriptionRaw: '', transcriptionTimestamps: [] });
                              }}
                              className='absolute top-2 right-2 p-1.5 bg-background/90 hover:bg-background rounded-full shadow-lg transition-colors'
                            >
                              <X className='h-4 w-4' />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Removed: Quiz display section - quiz generation happens on separate page */}
                {false && (
                  <div className='space-y-4 p-4 border rounded-lg bg-blue-50'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2'>
                        <CheckCircle2 className='h-5 w-5 text-blue-600' />
                        <label className='text-sm font-medium text-blue-900'>
                          Quiz Generated Successfully
                        </label>
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => setIsEditingQuiz(!isEditingQuiz)}
                          className='bg-white'
                        >
                          {isEditingQuiz ? (
                            <>
                              <Eye className='h-4 w-4 mr-1' />
                              View Mode
                            </>
                          ) : (
                            <>
                              <Edit2 className='h-4 w-4 mr-1' />
                              Edit Quiz
                            </>
                          )}
                        </Button>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          onClick={() => {
                            setQuizData(null);
                            setFormData({ ...formData, generateQuiz: false });
                            setIsEditingQuiz(false);
                          }}
                          className='text-blue-700 hover:text-blue-900'
                        >
                          <X className='h-4 w-4 mr-1' />
                          Remove Quiz
                        </Button>
                      </div>
                    </div>

                    <div className='space-y-4 bg-white p-4 rounded-md max-h-[600px] overflow-y-auto'>
                      {/* Quiz Title & Description */}
                      {isEditingQuiz ? (
                        <div className='space-y-3 pb-4 border-b'>
                          <div>
                            <label className='text-sm font-medium mb-1 block'>Quiz Title</label>
                            <Input
                              value={quizData.quizTitle}
                              onChange={(e) => setQuizData({ ...quizData, quizTitle: e.target.value })}
                              placeholder='Enter quiz title'
                            />
                          </div>
                          <div>
                            <label className='text-sm font-medium mb-1 block'>Quiz Description</label>
                            <Textarea
                              value={quizData.quizDescription}
                              onChange={(e) => setQuizData({ ...quizData, quizDescription: e.target.value })}
                              placeholder='Enter quiz description'
                              rows={2}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className='pb-4 border-b'>
                          <h4 className='font-semibold text-gray-900'>{quizData.quizTitle}</h4>
                          <p className='text-sm text-gray-600 mt-1'>{quizData.quizDescription}</p>
                        </div>
                      )}

                      {/* Questions List */}
                      <div className='space-y-4'>
                        <p className='text-sm font-medium text-gray-700'>
                          {quizData.questions?.length || 0} Questions
                        </p>

                        {quizData.questions?.map((question, qIndex) => (
                          <div key={qIndex} className='p-4 border rounded-lg bg-gray-50 space-y-3'>
                            {/* Question Header */}
                            <div className='flex items-start justify-between gap-2'>
                              <div className='flex-1'>
                                {isEditingQuiz ? (
                                  <div className='space-y-3'>
                                    <div className='flex items-center justify-between'>
                                      <label className='text-xs font-medium text-gray-500'>
                                        Question {qIndex + 1}
                                      </label>
                                      <Select
                                        value={question.type}
                                        onValueChange={(value) => {
                                          const newQuestions = [...quizData.questions];
                                          const oldType = newQuestions[qIndex].type;
                                          const cacheKey = `q${qIndex}`;

                                          // Cache the current state before changing type
                                          const newCache = { ...questionTypeCache };
                                          if (!newCache[cacheKey]) {
                                            newCache[cacheKey] = {};
                                          }

                                          // Save current type's data to cache
                                          newCache[cacheKey][oldType] = {
                                            options: newQuestions[qIndex].options ? [...newQuestions[qIndex].options] : undefined,
                                            correct_answer: newQuestions[qIndex].correct_answer
                                          };

                                          // Update question type
                                          newQuestions[qIndex].type = value;

                                          // Try to restore from cache first, otherwise create defaults
                                          if (newCache[cacheKey][value]) {
                                            // Restore cached data for this type
                                            newQuestions[qIndex].options = newCache[cacheKey][value].options;
                                            newQuestions[qIndex].correct_answer = newCache[cacheKey][value].correct_answer;
                                          } else {
                                            // No cache, create defaults
                                            if (value === 'multiple_choice') {
                                              newQuestions[qIndex].options = ['Option 1', 'Option 2', 'Option 3', 'Option 4'];
                                              newQuestions[qIndex].correct_answer = 'Option 1';
                                            } else if (value === 'true_false') {
                                              newQuestions[qIndex].options = undefined;
                                              newQuestions[qIndex].correct_answer = true;
                                            } else if (value === 'short_answer') {
                                              newQuestions[qIndex].options = undefined;
                                              newQuestions[qIndex].correct_answer = '';
                                            }
                                          }

                                          setQuestionTypeCache(newCache);
                                          setQuizData({ ...quizData, questions: newQuestions });
                                        }}
                                      >
                                        <SelectTrigger className='w-[180px] h-8 bg-white'>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value='multiple_choice'>Multiple Choice</SelectItem>
                                          <SelectItem value='true_false'>True/False</SelectItem>
                                          <SelectItem value='short_answer'>Text Answer</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <Textarea
                                      value={question.question}
                                      onChange={(e) => {
                                        const newQuestions = [...quizData.questions];
                                        newQuestions[qIndex].question = e.target.value;
                                        setQuizData({ ...quizData, questions: newQuestions });
                                      }}
                                      placeholder='Enter question text'
                                      rows={2}
                                      className='bg-white'
                                    />
                                  </div>
                                ) : (
                                  <div>
                                    <div className='flex items-center gap-2 mb-1'>
                                      <span className='text-xs font-medium text-gray-500'>
                                        Question {qIndex + 1}
                                      </span>
                                      <Badge variant='outline' className='text-xs'>
                                        {question.type === 'multiple_choice' ? 'Multiple Choice' :
                                         question.type === 'true_false' ? 'True/False' :
                                         'Text Answer'}
                                      </Badge>
                                    </div>
                                    <p className='text-sm font-medium text-gray-900 mt-1'>
                                      {question.question}
                                    </p>
                                  </div>
                                )}
                              </div>
                              {isEditingQuiz && (
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => {
                                    const newQuestions = quizData.questions.filter((_, i) => i !== qIndex);
                                    setQuizData({ ...quizData, questions: newQuestions });
                                  }}
                                  className='text-red-600 hover:text-red-700 hover:bg-red-50'
                                >
                                  <Trash2 className='h-4 w-4' />
                                </Button>
                              )}
                            </div>

                            {/* Answer Options */}
                            <div className='space-y-2'>
                              {/* Multiple Choice Questions */}
                              {question.options && question.options.length > 0 ? (
                                <>
                                  <label className='text-xs font-medium text-gray-500'>Options</label>
                                  {question.options.map((option, oIndex) => (
                                <div key={oIndex} className='flex items-center gap-2'>
                                  <div className={`flex-1 p-2 rounded border ${
                                    question.correct_answer === option
                                      ? 'bg-green-50 border-green-300'
                                      : 'bg-white border-gray-200'
                                  }`}>
                                    {isEditingQuiz ? (
                                      <div className='flex items-center gap-2'>
                                        <Input
                                          value={option}
                                          onChange={(e) => {
                                            const newQuestions = [...quizData.questions];
                                            newQuestions[qIndex].options[oIndex] = e.target.value;
                                            setQuizData({ ...quizData, questions: newQuestions });
                                          }}
                                          placeholder={`Option ${oIndex + 1}`}
                                          className='flex-1'
                                        />
                                        <Button
                                          type='button'
                                          variant='ghost'
                                          size='sm'
                                          onClick={() => {
                                            const newQuestions = [...quizData.questions];
                                            newQuestions[qIndex].correct_answer = option;
                                            setQuizData({ ...quizData, questions: newQuestions });
                                          }}
                                          className={`${
                                            question.correct_answer === option
                                              ? 'text-green-600'
                                              : 'text-gray-400 hover:text-green-600'
                                          }`}
                                          title='Mark as correct answer'
                                        >
                                          <CheckCircle2 className='h-4 w-4' />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className='flex items-center gap-2 text-sm'>
                                        {question.correct_answer === option && (
                                          <CheckCircle2 className='h-4 w-4 text-green-600 flex-shrink-0' />
                                        )}
                                        <span className={question.correct_answer === option ? 'font-medium' : ''}>
                                          {option}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {isEditingQuiz && question.options.length > 2 && (
                                    <Button
                                      type='button'
                                      variant='ghost'
                                      size='sm'
                                      onClick={() => {
                                        const newQuestions = [...quizData.questions];
                                        newQuestions[qIndex].options = newQuestions[qIndex].options.filter((_, i) => i !== oIndex);
                                        // If we deleted the correct answer, reset it
                                        if (newQuestions[qIndex].correct_answer === option) {
                                          newQuestions[qIndex].correct_answer = newQuestions[qIndex].options[0];
                                        }
                                        setQuizData({ ...quizData, questions: newQuestions });
                                      }}
                                      className='text-red-600 hover:text-red-700'
                                    >
                                      <X className='h-4 w-4' />
                                    </Button>
                                  )}
                                </div>
                              ))}

                                  {/* Add Option Button */}
                                  {isEditingQuiz && (
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      onClick={() => {
                                        const newQuestions = [...quizData.questions];
                                        newQuestions[qIndex].options.push('New option');
                                        setQuizData({ ...quizData, questions: newQuestions });
                                      }}
                                      className='w-full'
                                    >
                                      <Plus className='h-4 w-4 mr-1' />
                                      Add Option
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* True/False or Short Answer Questions */}
                                  <label className='text-xs font-medium text-gray-500'>
                                    {question.type === 'true_false' ? 'Answer' : 'Correct Answer'}
                                  </label>

                                  {isEditingQuiz ? (
                                    <div className='space-y-2'>
                                      {question.type === 'true_false' ? (
                                        <div className='flex gap-2'>
                                          <Button
                                            type='button'
                                            variant={question.correct_answer === true ? 'default' : 'outline'}
                                            size='sm'
                                            onClick={() => {
                                              const newQuestions = [...quizData.questions];
                                              newQuestions[qIndex].correct_answer = true;
                                              setQuizData({ ...quizData, questions: newQuestions });
                                            }}
                                            className='flex-1'
                                          >
                                            <CheckCircle2 className='h-4 w-4 mr-1' />
                                            True
                                          </Button>
                                          <Button
                                            type='button'
                                            variant={question.correct_answer === false ? 'default' : 'outline'}
                                            size='sm'
                                            onClick={() => {
                                              const newQuestions = [...quizData.questions];
                                              newQuestions[qIndex].correct_answer = false;
                                              setQuizData({ ...quizData, questions: newQuestions });
                                            }}
                                            className='flex-1'
                                          >
                                            <X className='h-4 w-4 mr-1' />
                                            False
                                          </Button>
                                        </div>
                                      ) : (
                                        <Textarea
                                          value={question.correct_answer || ''}
                                          onChange={(e) => {
                                            const newQuestions = [...quizData.questions];
                                            newQuestions[qIndex].correct_answer = e.target.value;
                                            setQuizData({ ...quizData, questions: newQuestions });
                                          }}
                                          placeholder='Enter the correct answer'
                                          rows={2}
                                          className='bg-white'
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <div className='p-3 rounded border bg-green-50 border-green-300'>
                                      <div className='flex items-center gap-2 text-sm font-medium text-green-900'>
                                        <CheckCircle2 className='h-4 w-4 text-green-600' />
                                        {question.type === 'true_false'
                                          ? (question.correct_answer ? 'True' : 'False')
                                          : question.correct_answer
                                        }
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Add Question Button */}
                        {isEditingQuiz && (
                          <Button
                            type='button'
                            variant='outline'
                            onClick={() => {
                              const newQuestion = {
                                question: 'New question',
                                type: 'multiple_choice',
                                options: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
                                correct_answer: 'Option 1'
                              };
                              setQuizData({
                                ...quizData,
                                questions: [...quizData.questions, newQuestion]
                              });
                            }}
                            className='w-full'
                          >
                            <Plus className='h-4 w-4 mr-1' />
                            Add Question
                          </Button>
                        )}
                      </div>
                    </div>

                    <p className='text-xs text-blue-700'>
                      This quiz will be shown to users after they watch the video
                    </p>
                  </div>
                )}

                {/* Removed: Retry Quiz Generation - quiz is now generated on separate page */}
                {false && formData.generateQuiz && formData.videoUrl && !quizData && !generatingQuiz && postType === 'video' && (
                  <div className='p-4 border rounded-lg bg-orange-50 border-orange-200'>
                    <div className='flex flex-col items-center gap-3 text-center'>
                      <div className='text-sm text-orange-700'>
                        Quiz generation failed or is still processing
                      </div>
                      <Button
                        type='button'
                        onClick={() => generateQuizFromAudio(null, formData.videoUrl)}
                        disabled={generatingQuiz}
                        className='gap-2 bg-orange-600 hover:bg-orange-700'
                      >
                        {generatingQuiz ? (
                          <>
                            <LoadingSpinner />
                            Generating Quiz...
                          </>
                        ) : (
                          <>
                            <RotateCcw className='h-4 w-4' />
                            Retry Quiz Generation
                          </>
                        )}
                      </Button>
                      <p className='text-xs text-orange-600'>
                        Audio URL: {formData.audioUrl?.substring(0, 60) || 'N/A'}...
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className='flex justify-end gap-2 pt-6 border-t'>
                  <Button
                    variant='outline'
                    onClick={handleCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || uploadingImage || uploadingVideo || transcribing}
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner />
                        {formData.generateQuiz ? 'Uploading...' : 'Creating...'}
                      </>
                    ) : transcribing ? (
                      <>
                        <LoadingSpinner />
                        Transcribing...
                      </>
                    ) : (
                      formData.generateQuiz ? (postType === 'text' ? 'Create & Generate Quiz' : 'Upload & Generate Quiz') : 'Create Post'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
